import { useCallback, useEffect, useRef, useState } from "react";
import { format, isToday, isYesterday } from "date-fns";
import { ChevronLeft, MessageSquareText, Trash2 } from "lucide-react";
import { supabase } from "../../../lib/supabase";
import {
  describeSendError,
  fetchMessages,
  hideMessage,
  sendMessage,
} from "../smsApi";
import { formatPhone, fullLineLabel, PHONE_LINES } from "../phone";
import { useCannedMessages } from "../useCannedMessages";
import type { SmsContact, SmsConversation, SmsMessage } from "../types";
import { ContactModal } from "./ContactModal";

const MAX_SMS_LENGTH = 1600;
// Belt-and-braces poll — Realtime is the primary delivery channel (see the
// `postgres_changes` subscription below). 60s gives us a fallback if Realtime
// ever drops a row without flooding PostgREST on an idle thread.
const POLL_INTERVAL_MS = 60_000;

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  if (isToday(d)) return format(d, "h:mm a");
  if (isYesterday(d)) return "Yesterday " + format(d, "h:mm a");
  return format(d, "MMM d, h:mm a");
}

function statusIcon(status: string | null): string {
  switch (status) {
    case "sent":
      return "✓";
    case "delivered":
      return "✓✓";
    case "failed":
      return "✗";
    default:
      return "";
  }
}

type Props = {
  contact: string;
  conversation: SmsConversation | null;
  contactInfo: SmsContact | null;
  isAdmin: boolean;
  onBack: () => void;
  onMessageSent: () => void;
  onContactAdded: () => void;
};

export function MessageThread({
  contact,
  conversation,
  contactInfo,
  isAdmin,
  onBack,
  onMessageSent,
  onContactAdded,
}: Props) {
  const [messages, setMessages] = useState<SmsMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // A failed load used to leave `messages` empty, which rendered "No messages
  // yet" — indistinguishable from a thread that genuinely has none. Staff would
  // read an outage as a quiet customer.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [fromNumber, setFromNumber] = useState(
    conversation?.phone_line === "line2"
      ? PHONE_LINES[1].number
      : PHONE_LINES[0].number
  );
  const [cannedOpen, setCannedOpen] = useState(false);
  const [hoveredMsgId, setHoveredMsgId] = useState<string | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);

  const cannedMessages = useCannedMessages();

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const messagesRef = useRef<SmsMessage[]>(messages);
  messagesRef.current = messages;

  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const reload = useCallback(async () => {
    try {
      const rows = await fetchMessages({ contact, limit: 200 });
      setMessages(rows.slice().reverse());
      setLoadError(null);
    } catch (err) {
      console.error("Failed to fetch messages", err);
      setLoadError((err as Error).message || "Could not load messages");
    } finally {
      setLoading(false);
    }
  }, [contact]);

  useEffect(() => {
    setLoading(true);
    setMessages([]);
    setSendError(null);
    reload();
  }, [reload]);

  useEffect(() => {
    if (!loading) scrollToBottom();
  }, [loading, messages.length, scrollToBottom]);

  // Realtime — INSERTs touching this contact get appended.
  // Track B cutover (April 22, 2026): flipped schema `public` → `sms`.
  useEffect(() => {
    const channel = supabase
      .channel(`hq-sms-thread-${contact}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "sms", table: "sms_messages" },
        (payload) => {
          const msg = payload.new as SmsMessage;
          const isRelevant =
            msg.from_number === contact || msg.to_number === contact;
          if (isRelevant) {
            setMessages((prev) => {
              if (prev.some((m) => m.id === msg.id)) return prev;
              return [...prev, msg];
            });
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [contact]);

  // Best-effort polling fallback if Realtime drops a row.
  useEffect(() => {
    if (loading) return;
    const interval = setInterval(async () => {
      const current = messagesRef.current;
      const latest = current[current.length - 1];
      const after = latest?.created_at ?? new Date(0).toISOString();
      try {
        const fresh = await fetchMessages({ contact, after });
        if (fresh.length > 0) {
          setMessages((prev) => {
            const newMsgs = fresh.filter((m) => !prev.some((p) => p.id === m.id));
            return newMsgs.length > 0 ? [...prev, ...newMsgs] : prev;
          });
        }
      } catch {
        // Best-effort only.
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [contact, loading]);

  async function handleSend() {
    const trimmed = body.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setSendError(null);
    try {
      await sendMessage({ to: contact, from: fromNumber, body: trimmed });
      setBody("");
      if (textareaRef.current) textareaRef.current.style.height = "auto";
      onMessageSent();
      reload();
    } catch (e) {
      setSendError(describeSendError(e));
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  function handleTextareaInput(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setBody(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  }

  function insertCanned(msg: string) {
    setBody(msg);
    setCannedOpen(false);
    textareaRef.current?.focus();
  }

  async function handleDeleteMessage(id: string) {
    setMessages((prev) => prev.filter((m) => m.id !== id));
    try {
      await hideMessage(id);
    } catch (err) {
      console.error("Failed to delete message", err);
      reload();
    }
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex flex-shrink-0 items-center gap-3 border-b border-tribal-border bg-tribal-surface px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="text-tribal-muted hover:text-white sm:hidden"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate font-semibold text-white">
              {contactInfo ? contactInfo.name : formatPhone(contact)}
            </p>
            {contactInfo && (
              <span className="flex-shrink-0 rounded bg-tribal-border px-1.5 py-0.5 text-[10px] text-tribal-muted">
                {contactInfo.list_type}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {contactInfo && (
              <p className="text-xs text-tribal-muted">{formatPhone(contact)}</p>
            )}
            {conversation?.phone_line && (
              <p className="text-xs text-tribal-muted">
                via {fullLineLabel(conversation.phone_line)}
              </p>
            )}
          </div>
        </div>
        {!contactInfo && isAdmin && (
          <button
            type="button"
            onClick={() => setShowContactModal(true)}
            className="flex-shrink-0 rounded-lg bg-tribal-border px-3 py-1.5 text-xs font-medium text-tribal-muted transition-colors hover:bg-white/10 hover:text-white"
          >
            + Add Contact
          </button>
        )}
      </header>

      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        {loading ? (
          <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
            <div className="animate-pulse">Loading messages…</div>
          </div>
        ) : loadError && messages.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-2 text-sm">
            <p className="font-medium text-red-300">Couldn't load messages</p>
            <p className="text-xs text-tribal-muted">{loadError}</p>
            <button
              onClick={() => { setLoading(true); reload(); }}
              className="mt-1 rounded border border-tribal-border px-3 py-1 text-xs text-white hover:bg-tribal-surface"
            >
              Retry
            </button>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-tribal-muted">
            <p>No messages yet</p>
          </div>
        ) : (
          messages.map((msg) => {
            const isOutbound = msg.direction === "outbound";
            const isHovered = hoveredMsgId === msg.id;
            return (
              <div
                key={msg.id}
                className={`flex items-end gap-1.5 ${
                  isOutbound ? "justify-end" : "justify-start"
                }`}
                onMouseEnter={() => setHoveredMsgId(msg.id)}
                onMouseLeave={() => setHoveredMsgId(null)}
              >
                {!isOutbound && (
                  <button
                    type="button"
                    onClick={() => handleDeleteMessage(msg.id)}
                    title="Delete message"
                    className={`flex-shrink-0 rounded-full p-1 text-tribal-muted transition-all hover:bg-white/10 hover:text-red-400 ${
                      isHovered ? "opacity-100" : "pointer-events-none opacity-0"
                    }`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}

                <div
                  className={`max-w-[75%] rounded-2xl px-4 py-2 ${
                    isOutbound
                      ? "rounded-br-md bg-tribal-red text-white"
                      : "rounded-bl-md bg-tribal-border text-gray-100"
                  }`}
                >
                  <p className="whitespace-pre-wrap break-words text-sm">
                    {msg.body}
                  </p>
                  <div
                    className={`mt-1 flex items-center gap-1 text-[10px] ${
                      isOutbound ? "justify-end text-red-100/90" : "text-tribal-muted"
                    }`}
                  >
                    <span>{formatTime(msg.created_at)}</span>
                    {isOutbound && (
                      <span className="font-medium">{statusIcon(msg.status)}</span>
                    )}
                  </div>
                </div>

                {isOutbound && (
                  <button
                    type="button"
                    onClick={() => handleDeleteMessage(msg.id)}
                    title="Delete message"
                    className={`flex-shrink-0 rounded-full p-1 text-tribal-muted transition-all hover:bg-white/10 hover:text-red-400 ${
                      isHovered ? "opacity-100" : "pointer-events-none opacity-0"
                    }`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="flex-shrink-0 border-t border-tribal-border bg-tribal-surface p-3">
        {sendError && (
          <p className="mb-2 rounded-lg bg-red-500/15 px-3 py-2 text-xs text-red-300">
            Failed to send: {sendError}
          </p>
        )}
        <div className="flex items-end gap-2">
          <select
            value={fromNumber}
            onChange={(e) => setFromNumber(e.target.value)}
            className="flex-shrink-0 rounded-lg border border-tribal-border bg-tribal-bg px-2 py-2 text-xs text-gray-300 focus:border-tribal-red focus:outline-none"
          >
            {PHONE_LINES.map((line) => (
              <option key={line.number} value={line.number}>
                {line.label}
              </option>
            ))}
          </select>

          <div className="relative flex-1">
            <textarea
              ref={textareaRef}
              value={body}
              onChange={handleTextareaInput}
              onKeyDown={handleKeyDown}
              placeholder="Type a message…"
              rows={1}
              className="w-full resize-none rounded-lg border border-tribal-border bg-tribal-bg px-3 py-2 text-sm text-white placeholder-tribal-muted focus:border-tribal-red focus:outline-none"
              style={{ maxHeight: 120 }}
            />
            {body.length > 0 && (
              <span
                className={`absolute bottom-1 right-2 text-[10px] ${
                  body.length > MAX_SMS_LENGTH ? "text-red-400" : "text-tribal-muted"
                }`}
              >
                {body.length}/{MAX_SMS_LENGTH}
              </span>
            )}
          </div>

          {cannedMessages.length > 0 && (
            <div className="relative flex-shrink-0">
              <button
                type="button"
                onClick={() => setCannedOpen((o) => !o)}
                title="Quick replies"
                className="rounded-lg border border-tribal-border bg-tribal-bg px-2 py-2 text-gray-300 transition-colors hover:bg-white/5 hover:text-white"
              >
                <MessageSquareText className="h-4 w-4" />
              </button>

              {cannedOpen && (
                <div className="absolute bottom-full right-0 z-10 mb-2 w-64 overflow-hidden rounded-xl border border-tribal-border bg-tribal-surface shadow-xl">
                  <p className="border-b border-tribal-border px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-tribal-muted">
                    Quick Replies
                  </p>
                  <div className="max-h-48 divide-y divide-tribal-border overflow-y-auto">
                    {cannedMessages.map((msg, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => insertCanned(msg)}
                        className="w-full px-3 py-2 text-left text-sm text-gray-200 transition-colors hover:bg-white/5"
                      >
                        {msg.length > 80 ? msg.slice(0, 80) + "…" : msg}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <button
            type="button"
            onClick={handleSend}
            disabled={!body.trim() || sending || body.length > MAX_SMS_LENGTH}
            className="flex-shrink-0 rounded-lg bg-tribal-red px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-tribal-red-dark disabled:cursor-not-allowed disabled:opacity-40"
          >
            {sending ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              "Send"
            )}
          </button>
        </div>
      </div>

      {showContactModal && (
        <ContactModal
          contact={{
            id: "",
            name: "",
            phone_number: contact,
            list_type: "Customer",
            notes: null,
            created_at: "",
          }}
          mode="create"
          onClose={() => setShowContactModal(false)}
          onSaved={() => {
            setShowContactModal(false);
            onContactAdded();
          }}
        />
      )}
    </div>
  );
}
